import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { Loading } from "@/components/common/PageHeader";
import { NAV } from "@/config/nav";
import { ShieldAlert } from "lucide-react";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/layout/Layout";
import Dashboard from "@/pages/Dashboard";
import Outlets from "@/pages/Outlets";
import Sales from "@/pages/Sales";
import Cash from "@/pages/Cash";
import Inventory from "@/pages/Inventory";
import Recipes from "@/pages/Recipes";
import Procurement from "@/pages/Procurement";
import Suppliers from "@/pages/Suppliers";
import Expenses from "@/pages/Expenses";
import Budget from "@/pages/Budget";
import PnL from "@/pages/PnL";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import Approvals from "@/pages/Approvals";
import Notifications from "@/pages/Notifications";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><Loading label="Loading Hunter" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function NoAccess() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center" data-testid="no-access">
      <ShieldAlert className="h-12 w-12 text-muted-foreground" strokeWidth={1.5} />
      <h2 className="font-display text-2xl font-bold tracking-tight">Access denied</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        You don't have permission to view this page. If you believe this is a mistake, contact your administrator.
      </p>
    </div>
  );
}

// Enforces permission on the route itself (not just sidebar visibility / direct-URL access).
function Guarded({ perm, children }) {
  const { can } = useAuth();
  if (perm && !can(perm)) return <NoAccess />;
  return children;
}

// Land on the first module the user is allowed to see.
function Home() {
  const { can } = useAuth();
  const first = NAV.find((n) => can(n.perm));
  return <Navigate to={first ? first.to : "/dashboard"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Guarded perm="dashboard:view"><Dashboard /></Guarded>} />
        <Route path="/outlets" element={<Guarded perm="outlets:view"><Outlets /></Guarded>} />
        <Route path="/sales" element={<Guarded perm="sales:view"><Sales /></Guarded>} />
        <Route path="/cash" element={<Guarded perm="cash:view"><Cash /></Guarded>} />
        <Route path="/inventory" element={<Guarded perm="inventory:view"><Inventory /></Guarded>} />
        <Route path="/recipes" element={<Guarded perm="recipes:view"><Recipes /></Guarded>} />
        <Route path="/procurement" element={<Guarded perm="procurement:view"><Procurement /></Guarded>} />
        <Route path="/suppliers" element={<Guarded perm="suppliers:view"><Suppliers /></Guarded>} />
        <Route path="/expenses" element={<Guarded perm="expenses:view"><Expenses /></Guarded>} />
        <Route path="/budget" element={<Guarded perm="budget:view"><Budget /></Guarded>} />
        <Route path="/pnl" element={<Guarded perm="pnl:view"><PnL /></Guarded>} />
        <Route path="/reports" element={<Guarded perm="reports:view"><Reports /></Guarded>} />
        <Route path="/users" element={<Guarded perm="users:view"><Users /></Guarded>} />
        <Route path="/roles" element={<Guarded perm="users:edit"><Roles /></Guarded>} />
        <Route path="/approvals" element={<Guarded perm="approvals:view"><Approvals /></Guarded>} />
        <Route path="/notifications" element={<Guarded perm="notifications:view"><Notifications /></Guarded>} />
        <Route path="/audit" element={<Guarded perm="audit:view"><Audit /></Guarded>} />
        <Route path="/settings" element={<Guarded perm="settings:view"><Settings /></Guarded>} />
      </Route>
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="*" element={<Protected><Home /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}
